import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { RiftboundIconButton } from '../ui/icon-button';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MdClose, MdTune } from 'react-icons/md';
import { RiftboundBottomSheet, SHEET_Z_FILTER } from '../ui/bottom-sheet';
import { AppFooter } from './AppFooter';
import {
  MOBILE_ACTION_FAB_SIZE_PX,
  MOBILE_FILTER_FAB_OPEN_Z,
  MOBILE_FILTER_FAB_Z,
  MOBILE_TAB_BAR_HEIGHT,
} from './MobileBottomNav';
import { MobileFilterFabBadge } from './MobileFilterFabBadge';
import {
  FilterSheetChromeContext,
  MobileFilterSheetContext,
  type MobileFilterSheetApi,
} from './MobileFilterSheetContext';
import { useCardPrintMobileFabInsetPx } from '../../views/card-print/useCardPrint';

interface MobileSidebarHostProps {
  /**
   * Optional: routes without a filter sidebar still mount this host so the
   * route-transition tree beneath it stays mounted across sidebar ↔ non-sidebar
   * navigations (otherwise the wrapper swap remounts it and kills the slide).
   */
  sidebar?: ReactNode;
  children: ReactNode;
  /** When true, lift FAB above the fixed bottom tab bar */
  showBottomTabs?: boolean;
}

export function MobileSidebarHost({
  sidebar,
  children,
  showBottomTabs = false,
}: MobileSidebarHostProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCountState] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setSheetOpen(false);
    setActiveFilterCountState(0);
  }, [location.pathname]);

  const setActiveFilterCount = useCallback((count: number) => {
    setActiveFilterCountState(count);
  }, []);

  const sheetApi = useMemo(
    (): MobileFilterSheetApi => ({
      openFilters: () => setSheetOpen(true),
      closeFilters: () => setSheetOpen(false),
      filtersOpen: sheetOpen,
      activeFilterCount,
      setActiveFilterCount,
    }),
    [sheetOpen, activeFilterCount, setActiveFilterCount],
  );

  const trayFabLift = useCardPrintMobileFabInsetPx();

  const fabBottom = showBottomTabs
    ? `calc(${MOBILE_TAB_BAR_HEIGHT + trayFabLift}px + env(safe-area-inset-bottom, 0px) + 12px)`
    : trayFabLift > 0
      ? `calc(${trayFabLift}px + env(safe-area-inset-bottom, 0px) + 16px)`
      : `calc(env(safe-area-inset-bottom, 0px) + 16px)`;

  const handleFloatingButtonClick = () => {
    setSheetOpen((prev) => !prev);
  };

  const filtersActive = activeFilterCount > 0;
  const fabAriaLabel = sheetOpen
    ? 'Close filters'
    : filtersActive
      ? `Open search and filters, ${activeFilterCount} active`
      : 'Open search and filters';

  const sheetTitle =
    filtersActive ? `Search & filters (${activeFilterCount})` : 'Search & filters';

  return (
    <MobileFilterSheetContext.Provider value={sheetApi}>
      <Box w="100%" minW={0} flex={1} minH={0} display="flex" flexDirection="column">
        {children}
      </Box>

      {sidebar ? (
        <>
      {/* FAB — tune when closed, X when filter sheet open; badge when filters applied */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 420, damping: 38 }}
        style={{
          position: 'fixed',
          bottom: fabBottom,
          right: 20,
          zIndex: sheetOpen ? MOBILE_FILTER_FAB_OPEN_Z : MOBILE_FILTER_FAB_Z,
          touchAction: 'manipulation',
        }}
      >
        <Box position="relative">
          <RiftboundIconButton
            aria-label={fabAriaLabel}
            size="lg"
            colorPalette="yellow"
            variant="solid"
            borderRadius="full"
            boxShadow="lg"
            w={`${MOBILE_ACTION_FAB_SIZE_PX}px`}
            h={`${MOBILE_ACTION_FAB_SIZE_PX}px`}
            type="button"
            onClick={handleFloatingButtonClick}
          >
            <AnimatePresence mode="wait" initial={false}>
              {sheetOpen ? (
                <motion.span
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <MdClose size={24} />
                </motion.span>
              ) : (
                <motion.span
                  key="tune"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <MdTune size={24} />
                </motion.span>
              )}
            </AnimatePresence>
          </RiftboundIconButton>
          {!sheetOpen ? <MobileFilterFabBadge count={activeFilterCount} /> : null}
        </Box>
      </motion.div>

      <RiftboundBottomSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        zIndex={SHEET_Z_FILTER}
        initialSnap={0}
        header={
          location.pathname === '/card-print' ? (
            <VStack gap={0.5} w="100%" px={2}>
              <Heading size="sm" w="100%" textAlign="center">
                {sheetTitle}
              </Heading>
              <Text fontSize="2xs" color="textMuted" textAlign="center" lineHeight="short">
                Find cards, build your tray, then review and download a PDF.
              </Text>
            </VStack>
          ) : (
            <Heading size="sm" w="100%" textAlign="center" px={2}>
              {sheetTitle}
            </Heading>
          )
        }
      >
        <Box px={1}>
          <FilterSheetChromeContext.Provider value={true}>
            {sidebar}
          </FilterSheetChromeContext.Provider>
          <Box borderTopWidth="hairline" borderColor="border" mt={6} pt={3} pb={1}>
            <AppFooter variant="sidebar" />
          </Box>
        </Box>
      </RiftboundBottomSheet>
        </>
      ) : null}
    </MobileFilterSheetContext.Provider>
  );
}
